import {
  Button as MaterialBtn,
  ButtonProps,
  CircularProgress,
} from '@mui/material';
import classNames from 'classnames';
import React, { CSSProperties, ElementType, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

export type ButtonModel = {
  htmlId: string;
  isLoading?: boolean;
  progressVariant?: 'determinate' | 'indeterminate';
  progressColor?: 'primary' | 'secondary' | 'inherit';
  progressValue?: number;
  progressSize?: number;
  progressStyles?: CSSProperties;
  component?: ElementType;
  className?: string;
  containerClass?: string;
};

interface StyleProps {
  isLoading: boolean;
  progressSize: number;
}

const useStyles = makeStyles<StyleProps>()((_, props) => ({
  root: {
    position: 'relative',
  },
  buttonContainer: {
    pointerEvents: props.isLoading ? 'none' : 'auto',
  },
  buttonContent: {
    opacity: props.isLoading ? 0 : 'initial',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: `-${props.progressSize / 2}px`,
    marginLeft: `-${props.progressSize / 2}px`,
  },
}));

const Button: React.FC<ButtonProps & ButtonModel> = (props) => {
  const [isAsyncClick, setIsAsyncClick] = useState<boolean>(false);

  const {
    children,
    isLoading = false,
    progressVariant = 'indeterminate',
    progressColor = 'secondary',
    progressValue = 0,
    progressStyles,
    progressSize = 18,
    onClick,
    disabled,
    htmlId,
    component = 'button',
    className,
    containerClass = '',
    ...rest
  } = props;

  const { classes } = useStyles({ isLoading, progressSize });
  // cases when we call async inside button click
  // e.g. onClick={async () => { await onClickHandler(); }}
  const onClickHandler = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (isLoading) {
      return;
    }
    if (!onClick || isAsyncClick) {
      return;
    }
    setIsAsyncClick(true);
    await Promise.resolve(onClick(event));
    setIsAsyncClick(false);
  };

  if (htmlId) {
    rest.id = htmlId;
  }

  return (
    <div
      className={classNames(classes.root, {
        [containerClass]: Boolean(containerClass),
      })}
    >
      <MaterialBtn
        {...rest}
        className={className}
        disabled={disabled}
        onClick={onClickHandler}
        component={component}
        classes={{ root: classes.buttonContainer }}
      >
        <div className={classes.buttonContent}>{children}</div>
      </MaterialBtn>
      {isLoading ? (
        <CircularProgress
          variant={progressVariant}
          color={progressColor}
          value={progressValue}
          style={{ ...progressStyles }}
          size={progressSize}
          className={classes.buttonProgress}
          data-testid={`${htmlId}-progress`}
        />
      ) : null}
    </div>
  );
};

export default Button;
